import moment from "moment-timezone"
import { seasonOfDay, seasonWindow } from "./plannerQuery"
import { organizerLabel, plural } from "./plannerLabels"

// The calendar views' arithmetic on the read's days (calendar days of the country, "2026-11-14"): weeks from Monday, the Season
// grid's rows and cells, the organizer timeline's bars, the holidays of a day, and the dates of a moved proposal.

const DAY = "YYYY-MM-DD"
const day = value => moment.utc(value, DAY, true)
export const addDays = (value, count) => day(value).add(count, "days").format(DAY)
export const daysBetween = (from, to) => day(to).diff(day(from), "days")
export const mondayOf = value => addDays(value, -((day(value).day() + 6) % 7))
const weekdayOf = value => day(value).day()

// The Mondays of a season's view, from the week of its first day to the week of its last (31 for Winter 2026/27).
export const seasonWeeks = season => {
  const { from, to } = seasonWindow(season)
  const weeks = []
  for (let week = mondayOf(from); week < to; week = addDays(week, 7)) weeks.push(week)
  return weeks
}

// The holidays the read draws (legal and declared), by day. Two holidays of one day are one day with both names (Phase A5).
export const holidaysByDay = holidays => {
  const days = new Map()
  ;(holidays || []).forEach(holiday => days.set(holiday.date, [...(days.get(holiday.date) || []), holiday]))
  return days
}
export const holidaysOfWeek = (holidays, monday) => {
  const sunday = addDays(monday, 6)
  return (holidays || []).filter(({ date }) => date >= monday && date <= sunday)
}

// A row of the Season view: by region (Romania; Bulgaria by oblast, the club's county) or by organizer, as the read names them.
const NO_ROW = "none"
const placeRow = country => item => {
  const name = country === "Bulgaria" ? item.county : item.region
  return name ? { key: name, label: name } : { key: NO_ROW, label: country === "Bulgaria" ? "No oblast" : "No region" }
}
const organizerRow = item => ({ key: item.organizer?.id || NO_ROW, label: organizerLabel(item.organizer) })
const STATE_ORDER = ["published", "confirmed", "proposed", "draft", "refused", "withdrawn"]
const STATE_WORDS = { published: "published", confirmed: "confirmed", proposed: "proposed", draft: "draft", refused: "declined", withdrawn: "withdrawn" }

// The Season view's rows: each with the tournaments of every week, by the week they start in. Regions in alphabetical order,
// organizers with the most tournaments first; the row without a region or an organizer last.
export const seasonRows = (items, weeks, { group, country }) => {
  const rowOf = group === "organizer" ? organizerRow : placeRow(country)
  const rows = new Map()
  items.forEach(item => {
    const { key, label } = rowOf(item)
    if (!rows.has(key)) rows.set(key, { key, label, items: [] })
    rows.get(key).items.push(item)
  })
  const shown = new Set(weeks)
  return [...rows.values()].map(row => {
    const cells = new Map(weeks.map(week => [week, []]))
    row.items.forEach(item => { const week = mondayOf(item.days.start); if (shown.has(week)) cells.get(week).push(item) })
    const counts = STATE_ORDER.map(state => [state, row.items.filter(item => item.state === state).length]).filter(([, count]) => count)
    const sub = group === "organizer" ? [String(row.items.length), ...counts.map(([state, count]) => `${count} ${STATE_WORDS[state]}`)].join(" · ")
      : plural(new Set(row.items.map(item => item.organizer?.id || NO_ROW)).size, "organizer")
    return { ...row, cells, sub, none: row.key === NO_ROW }
  }).sort((a, b) => a.none - b.none || (group === "organizer" ? b.items.length - a.items.length : 0) || a.label.localeCompare(b.label))
}

// The organizer timeline: a row's tournaments as bars from day `first` (a Monday) over `count` days, each on the first lane free.
export const timelineBars = (items, first, count) => {
  const lanes = []
  return [...items].sort((a, b) => a.days.start.localeCompare(b.days.start) || b.days.end.localeCompare(a.days.end)).map(item => {
    const from = Math.max(0, daysBetween(first, item.days.start)), to = Math.min(count - 1, daysBetween(first, item.days.end))
    if (to < 0 || from > count - 1) return null
    let lane = lanes.findIndex(end => end < from)
    if (lane < 0) lane = lanes.push(to) - 1
    else lanes[lane] = to
    return { item, from, to, lane }
  }).filter(Boolean)
}

// A click on an empty day proposes that weekend (Saturday–Sunday) on a weekend day, else that day (ticket 15 Q11).
export const newProposalDays = value => {
  if (weekdayOf(value) === 6) return { start: value, end: addDays(value, 1) }
  if (weekdayOf(value) === 0) return { start: addDays(value, -1), end: value }
  return { start: value, end: value }
}

// A proposal moved by `delta` days keeps its times of day in its country's time zone (B3: a changed day keeps the stored time).
export const movedBy = (item, delta, timeZone) => ({
  startDate: moment(item.startDate).tz(timeZone).add(delta, "days").toISOString(),
  endDate: moment(item.endDate).tz(timeZone).add(delta, "days").toISOString(),
  days: { start: addDays(item.days.start, delta), end: addDays(item.days.end, delta) },
})
// The edit a move sends (Phase A6): the new start and end, and the season when the start crosses into another one.
export const moveChanges = (from, to) => ({ startDate: to.startDate, endDate: to.endDate,
  ...(seasonOfDay(to.days.start) !== seasonOfDay(from.days.start) && { season: seasonOfDay(to.days.start) }) })
// A published event moved by whole weeks (Phase E3, ticket 14), as the server moves it: its days, start and end, registration opening and
// competition starts, each at its local time in the event's zone.
export const eventMovedBy = (event, weeks, timeZone) => {
  const zone = event.timezone || timeZone
  const later = instant => (instant ? moment(instant).tz(zone).add(weeks, "weeks").toISOString() : instant)
  return { ...movedBy(event, 7 * weeks, zone), registrationsOpenAt: later(event.registrationsOpenAt),
    competitions: (event.competitions || []).map(competition => ({ ...competition, startDate: later(competition.startDate) })) }
}
// The whole weeks a drop moves a published event: to the same weekday of the week it lands in (ticket 14), whatever day of it the Month
// view drops it on.
export const weeksMoved = (event, delta) => daysBetween(mondayOf(event.days.start), mondayOf(addDays(event.days.start, delta))) / 7
// The tournaments moved and not yet read again, by key, shown at their new dates: a move holds while the read still has the revision it
// moved (a published event's dates revision, Phase E3).
export const applyMoves = (items, moves) => items.map(item => {
  const move = moves[item.key]
  return move && move.revision === (item.kind === "event" ? item.datesRevision : item.revision) ? { ...item, ...move.to, moving: true } : item
})
